import type { TaskTreeNode } from "@app/tasks/types";

/** Иконки известных корневых папок (совпадают со списочным видом). */
export const TASK_FOLDER_ICONS: Readonly<Record<string, string>> = {
    "Учёт в производстве": "/images/common/factory.svg",
    "Складской учёт": "/images/common/warehouse.svg",
};

/**
 * Узел — папка, если у него есть дочерние узлы.
 * @param node узел дерева задач
 * @returns `true`, если узел является папкой
 */
export function isFolderNode(node: TaskTreeNode): boolean {
    return (node.children?.length ?? 0) > 0;
}

/**
 * Разрешает URL иконки узла: `task.Icon` или известная папка из {@link TASK_FOLDER_ICONS}.
 * @param node узел дерева задач
 * @returns путь к SVG или `undefined`, если иконки нет
 */
export function resolveNodeIcon(node: TaskTreeNode): string | undefined {
    if (node.data.task.Icon != null && node.data.task.Icon !== "") {
        return node.data.task.Icon;
    }

    return TASK_FOLDER_ICONS[node.name];
}

/**
 * Ищет узел по `id` в дереве (DFS).
 * @param nodes корень или поддерево
 * @param id идентификатор узла
 * @returns найденный узел или `null`
 */
export function findNodeById(nodes: readonly TaskTreeNode[], id: string): TaskTreeNode | null {
    for (const node of nodes) {
        if (node.id === id) {
            return node;
        }

        if (node.children != null && node.children.length > 0) {
            const found = findNodeById(node.children, id);
            if (found != null) {
                return found;
            }
        }
    }

    return null;
}

/**
 * Возвращает `id` родителя узла или `null`, если узел на корневом уровне / не найден.
 * @param nodes корень дерева
 * @param childId идентификатор дочернего узла
 * @returns `id` родителя или `null`
 */
export function findParentId(nodes: readonly TaskTreeNode[], childId: string): string | null {
    for (const node of nodes) {
        if (node.children?.some((child) => child.id === childId) === true) {
            return node.id;
        }

        if (node.children != null && node.children.length > 0) {
            const found = findParentId(node.children, childId);
            if (found != null) {
                return found;
            }
        }
    }

    return null;
}

/**
 * Узлы текущего уровня drill-down: корень или дети папки `folderId`.
 * @param nodes корень дерева
 * @param folderId `id` текущей папки или `null` для корня
 * @returns список узлов для отображения
 */
export function getNodesAtLevel(nodes: readonly TaskTreeNode[], folderId: string | null): TaskTreeNode[] {
    if (folderId == null) {
        return [...nodes];
    }

    const folder = findNodeById(nodes, folderId);
    return folder?.children != null ? [...folder.children] : [];
}
